import React, { useState, useEffect } from 'react';
import './App.css';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import FixedExpensesManagement from './components/FixedExpensesManagement';
import Metas from './components/Metas';
import Simulator from './components/Simulator';
import Reports from './components/Reports';
import Bolsillos from './components/Bolsillos';
import HowToUse from './components/HowToUse';
import FinanceAI from './components/FinanceAI';
import Deudas from './components/Deudas';
import MeDeben from './components/MeDeben';
import Compras from './components/Compras';
import Negocio from './components/Negocio';
import Envios from './components/Envios';
import Comprobantes from './components/Comprobantes';
import MiDia from './components/MiDia';
import Presupuestos from './components/Presupuestos';
import RegistroDiario from './components/RegistroDiario';

function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('registro');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCambiarClave, setShowCambiarClave] = useState(false);
  const [nuevaClave, setNuevaClave] = useState('');
  const [confirmarClave, setConfirmarClave] = useState('');
  const [claveMsg, setClaveMsg] = useState({ tipo: '', texto: '' });
  const [guardandoClave, setGuardandoClave] = useState(false);

  // Auth + escuchar evento de recuperación de contraseña
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setAuthLoading(false);
      return;
    }
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY') {
        // El usuario llegó desde el enlace de recuperación — abrir modal directamente
        setShowCambiarClave(true);
        setClaveMsg({ tipo: '', texto: '' });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Cargar perfil
  useEffect(() => {
    if (!session?.user) { setProfile(null); return; }
    supabase.from('profiles').select('*').eq('id', session.user.id).single()
      .then(({ data }) => setProfile(data));
  }, [session]);

  const handleCambiarClave = async (e) => {
    e.preventDefault();
    if (nuevaClave.length < 6) {
      setClaveMsg({ tipo: 'error', texto: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (nuevaClave !== confirmarClave) {
      setClaveMsg({ tipo: 'error', texto: 'Las contraseñas no coinciden.' });
      return;
    }
    setGuardandoClave(true);
    const { error } = await supabase.auth.updateUser({ password: nuevaClave });
    if (error) {
      setClaveMsg({ tipo: 'error', texto: 'Error: ' + error.message });
    } else {
      setClaveMsg({ tipo: 'ok', texto: '¡Contraseña actualizada! Ya puedes usar la nueva.' });
      setNuevaClave('');
      setConfirmarClave('');
      setTimeout(() => { setShowCambiarClave(false); setClaveMsg({ tipo: '', texto: '' }); }, 2500);
    }
    setGuardandoClave(false);
  };

  const handleSignOut = async () => {
    if (isSupabaseConfigured()) await supabase.auth.signOut();
    setSession(null);
  };

  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (isSupabaseConfigured() && !session) {
    return <Auth />;
  }

  const userId = session?.user?.id || 'demo';
  const userName = profile?.nombre || session?.user?.email?.split('@')[0] || 'Usuario';

  const navItems = [
    { key: 'registro',      label: 'Registro Diario', icon: '📝' },
    { key: 'bolsillos',     label: 'Mis Categorías', icon: '📂' },
    { key: 'dashboard',     label: 'Dashboard',      icon: '📊' },
    { key: 'dia',           label: 'Mi Día',         icon: '📅' },
    { key: 'presupuestos',  label: 'Presupuestos',   icon: '🧺' },
    { key: 'metas',         label: 'Mis Metas',      icon: '🎯' },
    { key: 'fixedExpenses', label: 'Gastos Fijos',   icon: '🏠' },
    { key: 'simulator',     label: 'Simulador',      icon: '🔮' },
    { key: 'reports',       label: 'Reportes',       icon: '📈' },
    { key: 'deudas',        label: 'Mis Deudas',     icon: '💳' },
    { key: 'medeben',       label: 'Me Deben',       icon: '🤝' },
    { key: 'compras',       label: 'Mis Compras',    icon: '🛒' },
    { key: 'negocio',       label: 'Mi Negocio',     icon: '🏷️' },
    { key: 'envios',        label: 'Envíos Flex',    icon: '🚚' },
    { key: 'comprobantes',  label: 'Comprobantes',   icon: '📎' },
    { key: 'howto',         label: 'Cómo usar',      icon: '📖' },
  ];

  const renderComponent = () => {
    switch (activeTab) {
      case 'registro':      return <RegistroDiario userId={userId} />;
      case 'bolsillos':     return <Bolsillos userId={userId} />;
      case 'dashboard':     return <Dashboard userId={userId} />;
      case 'dia':           return <MiDia userId={userId} />;
      case 'presupuestos':  return <Presupuestos userId={userId} />;
      case 'metas':         return <Metas userId={userId} />;
      case 'fixedExpenses': return <FixedExpensesManagement userId={userId} />;
      case 'simulator':     return <Simulator userId={userId} />;
      case 'reports':       return <Reports userId={userId} />;
      case 'deudas':        return <Deudas userId={userId} />;
      case 'medeben':       return <MeDeben userId={userId} />;
      case 'compras':       return <Compras userId={userId} />;
      case 'negocio':       return <Negocio userId={userId} />;
      case 'envios':        return <Envios userId={userId} />;
      case 'comprobantes':  return <Comprobantes userId={userId} />;
      case 'howto':         return <HowToUse />;
      default:              return <Bolsillos userId={userId} />;
    }
  };

  return (
    <div className="app">
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />
      )}
      <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="var(--primary)"/>
              <path d="M10 18 Q11 10 16 9 Q21 10 22 18" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
              <circle cx="16" cy="20" r="2.5" fill="white"/>
              <path d="M12 24 L20 24" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="sidebar-app-name">En Cintura</span>
          </div>
          <div className="sidebar-user">
            <div className="user-avatar">{userName.charAt(0).toUpperCase()}</div>
            <span className="user-name">{userName}</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button
              key={item.key}
              className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(item.key); setIsMobileMenuOpen(false); }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button className="cambiar-clave-btn" onClick={() => { setShowCambiarClave(true); setClaveMsg({ tipo: '', texto: '' }); }}>
            <span>🔑</span>
            <span>Cambiar contraseña</span>
          </button>
          <button className="signout-btn" onClick={handleSignOut}>
            <span>↩</span>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <button
          className="hamburger-menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
        <div className="content-wrapper">
          {renderComponent()}
        </div>
      </main>

      <FinanceAI userId={userId} userName={userName} />

      {/* Modal: Cambiar contraseña */}
      {showCambiarClave && (
        <div className="modal-overlay" onClick={() => setShowCambiarClave(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔑 Cambiar contraseña</h3>
              <button className="modal-close" onClick={() => setShowCambiarClave(false)}>×</button>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Solo tú sabrás tu nueva contraseña. Elige algo que recuerdes bien.
            </p>
            <form onSubmit={handleCambiarClave}>
              <div className="form-group">
                <label>Nueva contraseña</label>
                <input type="password" value={nuevaClave} onChange={e => setNuevaClave(e.target.value)}
                  placeholder="Mínimo 6 caracteres" minLength={6} required autoFocus />
              </div>
              <div className="form-group">
                <label>Confirmar contraseña</label>
                <input type="password" value={confirmarClave} onChange={e => setConfirmarClave(e.target.value)}
                  placeholder="Repite la contraseña" required />
              </div>
              {claveMsg.texto && (
                <div className={claveMsg.tipo === 'ok' ? 'auth-success' : 'auth-error'} style={{ marginBottom: 16 }}>
                  {claveMsg.texto}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCambiarClave(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardandoClave}>
                  {guardandoClave ? 'Guardando...' : 'Guardar contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
